import React, { useContext, useMemo } from 'react';

import RecordsGrid from 'components/Grid/RecordsGrid';
import CardContext from 'contexts/CardContext';
import CustomerLaneSlideOutContext from 'contexts/CustomerLaneSlideOut';
import GridProvider from '../../../contextProviders/GridProvider';
import CommitmentQueryContext from '../../../contexts/CommitmentQueryContext';
import BaseViewsContext from '../../../contexts/BaseViewsContext';
import useBoardBaseView from '../../../hooks/useBoardBaseView';
import BoardContext from '../../../contexts/BoardContext';
import useWindowSize from '../../../hooks/useWindowSize';
import { CUSTOMER_LANE_COMMITMENTS, Z_INDEX } from '../../../constants';
import isPerformanceBoard from '../../../isPerformanceBoard';
import isCustomerLaneBoard from '../../../isCustomerLaneBoard';
import CustomerLaneSlideOut from '../SlideOut';

const GridContainer = ({ baseView }: { baseView: FleetOps.BaseView }) => {
  const { isMobile } = useWindowSize();
  const { board } = useContext(BoardContext);
  const { getContextMenuItems } = useContext(CommitmentQueryContext);
  const { isCustomerLaneSlideOpen } = useContext(CustomerLaneSlideOutContext);
  const newBaseView = useBoardBaseView(baseView);

  const boardSortBy = isCustomerLaneBoard(board) ? board.sortBy : undefined;
  const sortOverride = useMemo(
    () => (boardSortBy ? [boardSortBy] : undefined),
    [boardSortBy],
  );

  if (isPerformanceBoard(board)) {
    return null;
  }
  const isSecondColumnFitted = !!board.isSizedToFitOverridden && !isMobile;

  return (
    <CardContext.Provider
      value={{
        i: '',
        w: 1,
        h: 1,
      }}
    >
      <GridProvider
        dataType={CUSTOMER_LANE_COMMITMENTS}
        baseViewOverride={newBaseView}
        sortOverride={sortOverride}
      >
        <RecordsGrid
          surface="customerLaneBoard"
          // AG fits the second column (the lane, beside the customer) to its
          // content and the others share the rest when the board asks, else
          // sizes every column to its cells
          sizing={isSecondColumnFitted ? 'fitWidth' : 'fitContent'}
          skipHeaderWhenSizing={!isSecondColumnFitted}
          slideOut={
            isCustomerLaneSlideOpen && (
              <div
                style={{
                  height: '100%',
                  minWidth: 500,
                  width: '35%',
                  zIndex: Z_INDEX.GRID_SLIDE_OUT,
                  fontSize: 11,
                }}
              >
                <CustomerLaneSlideOut />
              </div>
            )
          }
          isBoard
          getContextMenuItems={getContextMenuItems}
          layoutOnFirstRender={true}
          layoutOnColumnChange={false}
          layoutOnModelUpdated={false}
          autoSizeSecondColumnOnly={isSecondColumnFitted}
          hasBorders={true}
          hasDarkHeader={true}
        />
      </GridProvider>
    </CardContext.Provider>
  );
};

const Gate = () => {
  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[CUSTOMER_LANE_COMMITMENTS];

  if (baseView) {
    return <GridContainer baseView={baseView} />;
  } else {
    return null;
  }
};

export default Gate;
